import { cambiarPasswordSchema, type CambiarPasswordInput } from '@app/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { authApi } from '../api/auth.api';

export function CambiarPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const es = useI18n();
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CambiarPasswordInput>({ resolver: zodResolver(cambiarPasswordSchema) });

  const mutation = useMutation({
    mutationFn: authApi.cambiarPassword,
    onSuccess: () => {
      setFeedback({ kind: 'success', msg: es.auth.cambiarPassword.exito });
      void queryClient.invalidateQueries({ queryKey: qk.auth.me });
      setTimeout(() => navigate('/', { replace: true }), 1500);
    },
    onError: (error) => {
      setFeedback({ kind: 'error', msg: getErrorMessage(error, es.errores.generico.algoSalioMal) });
    },
  });

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-marca-neutro/30 px-4">
      <div className="w-full max-w-md">
        <form
          onSubmit={(e) => void handleSubmit((d) => mutation.mutate(d))(e)}
          className="flex flex-col gap-4 rounded-lg border border-marca-neutro bg-surface p-6 shadow-sm"
          noValidate
        >
          <h2 className="text-xl font-semibold text-marca-gris">{es.auth.cambiarPassword.titulo}</h2>
          <p className="text-sm text-marca-gris/70">{es.auth.cambiarPassword.descripcion}</p>

          {feedback && <Alert variant={feedback.kind}>{feedback.msg}</Alert>}

          <div className="flex flex-col gap-2">
            <Label htmlFor="passwordActual">{es.auth.cambiarPassword.passwordActual}</Label>
            <Input
              id="passwordActual"
              type="password"
              autoComplete="current-password"
              aria-invalid={!!errors.passwordActual}
              {...register('passwordActual')}
            />
            {errors.passwordActual && (
              <span className="text-xs text-danger">{errors.passwordActual.message}</span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="passwordNuevo">{es.auth.cambiarPassword.passwordNuevo}</Label>
            <Input
              id="passwordNuevo"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!errors.passwordNuevo}
              {...register('passwordNuevo')}
            />
            {errors.passwordNuevo && (
              <span className="text-xs text-danger">{errors.passwordNuevo.message}</span>
            )}
            <span className="text-xs text-marca-gris/60">{es.auth.cambiarPassword.politicaAyuda}</span>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="passwordNuevoConfirmacion">
              {es.auth.cambiarPassword.passwordNuevoConfirmacion}
            </Label>
            <Input
              id="passwordNuevoConfirmacion"
              type="password"
              autoComplete="new-password"
              aria-invalid={!!errors.passwordNuevoConfirmacion}
              {...register('passwordNuevoConfirmacion')}
            />
            {errors.passwordNuevoConfirmacion && (
              <span className="text-xs text-danger">{errors.passwordNuevoConfirmacion.message}</span>
            )}
          </div>

          <Button type="submit" disabled={isSubmitting || mutation.isPending}>
            {mutation.isPending ? es.auth.cambiarPassword.guardando : es.auth.cambiarPassword.guardar}
          </Button>
        </form>
      </div>
    </div>
  );
}
